import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { ifProp, prop } from 'styled-tools'
import { palette } from 'styled-theme'
import { Box, Flex } from '@rebass/grid'
import Link from '../../atoms/Link'
import Image from '../../atoms/Image'
import Text, { formatLineBreak } from '../../atoms/Text'
import { datetimeFormatter } from '../../../utlils/datetime'

const REGEX_URL = /http[s]*:\/\/[^\s]+/g

const StyledBox = styled(Box)`
  background: ${ifProp({ align: 'left' }, palette('primary', 3), palette('primary', 5))};
  border-radius: 0.5rem;
  padding: 0.8rem 0.8rem 0.4rem;
  position: relative;
  box-shadow: 0 0.125rem 0.125rem ${palette('grayscale', 2)};
  word-break: break-word;
  max-width: 80%;
  min-width: 30%;
  ${ifProp({ align: 'left' }, css`
    border-top-left-radius: 0;
    margin-left: 0.6rem;
  `, css`
    border-top-right-radius: 0;
    margin-right: 0.6rem;
  `)}
  ::before {
    content: '';
    position: absolute;
    top: 0;
    border-bottom: 1rem solid transparent;
    ${ifProp({ align: 'left' }, css`
      left: 0.2rem;
      border-right: 0.8rem solid ${palette('primary', 3)};
      margin-left: -0.8rem;
    `, css`
      right: 0.2rem;
      border-left: 0.8rem solid ${palette('primary', 5)};
      margin-right: -0.8rem;
    `)}
  }
`

const ReplyContainer = styled(Flex)`
  flex-direction: column;
  padding: 0.4rem;
  border-radius: 0.4rem;
  ${ifProp({ align: 'left' }, css`
    background-color: ${palette('primary', 5)}80;
    border-left: 0.3rem solid ${palette('primary', 5)};
  `, css`
    background-color: ${palette('primary', 3)}33;
    border-left: 0.3rem solid ${palette('primary', 3)}CC;
  `)}
`

const ImagesContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(${(prop('columns', 2))}, 1fr);
  place-items: center;
  gap: 0.4rem;
`

const LoadingDots = styled.div`
  width: 40px;
  aspect-ratio: 2;
  --_g: no-repeat radial-gradient(circle closest-side, #F5F5F5 90%, #F5F5F500);
  background: 
    var(--_g) 0%   50%,
    var(--_g) 50%  50%,
    var(--_g) 100% 50%;
  background-size: calc(100%/3) 50%;
  animation: l3 1s infinite linear;
  @keyframes l3 {
    20%{background-position:0%   0%, 50%  50%,100%  50%}
    40%{background-position:0% 100%, 50%   0%,100%  50%}
    60%{background-position:0%  50%, 50% 100%,100%   0%}
    80%{background-position:0%  50%, 50%  50%,100% 100%}
  }
`

const Message = ({ message, align, onImageLoad, ...props }) => {
  const { sender = {}, content, reply_message, message_images = [], send_time } = message
  const { display_name } = sender
  const matchResults = (content || '').match(REGEX_URL)

  const isBotTyping = useMemo(() => content === '正在輸入中...', [content])

  const sortedImages = useMemo(() => {
    return message_images.sort((a, b) => a.sequence - b.sequence)
  }, [message_images])

  if (isBotTyping) {
    return (
      <StyledBox
        align="left"
        style={{ padding: '0.5rem 1rem', minWidth: 'auto' }}
        {...props}
      >
        <LoadingDots />
      </StyledBox>
    )
  }

  return (
    <StyledBox align={align} {...props}>
      {align === 'left' && (
        <Flex mb="0.4rem">
          <Text bold variant="body1" palette="grayscale" paletteIndex={align === 'left' ? 4 : 1}>{display_name}</Text>
        </Flex>
      )}
      <Box>
        {reply_message && (
          <ReplyContainer align={align} mb="0.4rem">
            <Flex mb="0.4rem">
              <Text bold variant="body1" palette="grayscale" paletteIndex={align === 'left' ? 1 : 1}>
                {reply_message.sender?.display_name}
              </Text>
            </Flex>
            {reply_message.message_images?.length > 0 && (
              <Flex mb="0.2rem" style={{ gap: '0.4rem' }}>
                {reply_message.message_images.map((image, index) => (
                  <Image
                    key={index}
                    src={image.image_url}
                    width="auto"
                    height="50px"
                    borderRadius="0.4rem"
                    style={{ objectFit: 'contain' }}
                  />
                ))}
              </Flex>
            )}
            <Text
              variant="body2"
              palette="grayscale"
              paletteIndex={1}
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {reply_message.content}
            </Text>
          </ReplyContainer>
        )}
        {sortedImages.length > 0 && (
          <ImagesContainer
            columns={Math.min(sortedImages.length, 2)}
            mb="0.4rem"
          >
            {sortedImages.map((image, index) => (
              <Image
                key={index}
                src={image.image_url}
                width="100%"
                height="auto"
                borderRadius="0.5rem"
                onLoad={onImageLoad}
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center',
                  maxWidth: 'min(400px, 50vw)',
                  maxHeight: 'min(533px, 66vw)',
                }}
              />
            ))}
          </ImagesContainer>
        )}
        {!!content && (
          <Text variant="body1" palette="grayscale" paletteIndex={align === 'left' ? 5 : 0}>
            {matchResults?.length ? (
              (content || '').split(REGEX_URL).map((str, j) => (
                <React.Fragment key={j}>
                  {formatLineBreak(str)}
                  {j < matchResults.length && (
                    <Link
                      href={matchResults[j]}
                      palette={align === 'left' ? 'secondary' : 'primary'}
                    >
                      {matchResults[j]}
                    </Link>
                  )}
                </React.Fragment>
              ))
            ) : content}
          </Text>
        )}
      </Box>
      <Flex justifyContent="flex-end" mt="0.2rem">
        <Text variant="body4" palette="grayscale" paletteIndex={align === 'left' ? 4 : 2}>
          {datetimeFormatter(send_time, 'h:mm A')}
        </Text>
      </Flex>
    </StyledBox>
  )
}

Message.propTypes = {
  message: PropTypes.shape({
    sender: PropTypes.shape({
      display_name: PropTypes.string,
    }),
    content: PropTypes.string,
    reply_message: PropTypes.shape({
      sender: PropTypes.shape({
        display_name: PropTypes.string,
      }),
      message_images: PropTypes.arrayOf(PropTypes.object),
      content: PropTypes.string,
    }),
    message_images: PropTypes.arrayOf(
      PropTypes.shape({
        image_url: PropTypes.string,
        sequence: PropTypes.number,
      }),
    ),
    send_time: PropTypes.number,
  }),
  align: PropTypes.oneOf(['left', 'right']),
  onImageLoad: PropTypes.func,
}

Message.defaultProps = {
  message: {},
  align: 'left',
  onImageLoad: () => {},
}

export default Message
